import { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success("If that email exists, a reset link has been issued.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex crew-login-hero items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-slate-100">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Anchor className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>CrewOnShip</div>
        </div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Forgot password</h2>
        <p className="text-sm text-slate-500 mt-1">
          Enter your email and we'll help you reset access. Your Management team may also reset your temporary password directly.
        </p>

        {sent ? (
          <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="forgot-password-sent">
            If that email exists, a reset link has been issued. Please check your inbox or contact Management.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-9 h-11"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#0b192c] hover:bg-[#0f2544]"
              data-testid="forgot-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-cyan-700 hover:text-cyan-800 font-medium">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
